import { useEffect, useMemo, useState } from "react";
import { GeoJSON } from "react-leaflet";
import type { FeatureCollection, Feature } from "geojson";
import { useUIStore, pushUrl } from "../../store/uiStore";

interface CountyProps {
  county_geoid: string;
  county: string | null;
  max_p_hat: number | null;
  max_p_tilde: number | null;            // raw routing score (can exceed 1) — kept for back-compat
  max_p_tilde_display: number | null;    // sigmoid display, always in [0,1] — used for UI
  tract_count: number | null;
}

const RAMP = ["#fde68a", "#fb923c", "#ef4444", "#b91c1c", "#7f1d1d"];

function quintileBreaks(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 0) return [0, 0.25, 0.5, 0.75, 1];
  return [0.2, 0.4, 0.6, 0.8].map((q) =>
    sorted[Math.floor(q * (sorted.length - 1))],
  );
}

function makeColorFn(breaks: number[]): (v: number | null) => string {
  return (v: number | null) => {
    if (v == null || isNaN(v)) return "#e5e7eb";  // muted gray (R8)
    if (v >= breaks[3]) return RAMP[4];
    if (v >= breaks[2]) return RAMP[3];
    if (v >= breaks[1]) return RAMP[2];
    if (v >= breaks[0]) return RAMP[1];
    return RAMP[0];
  };
}

export function CountyChoropleth() {
  const { choroplethMode, setDrillCounty } = useUIStore();
  const [data, setData] = useState<FeatureCollection | null>(null);

  useEffect(() => {
    fetch("/counties.geojson")
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, []);

  // Compute per-mode quintile breaks from actual data so both raw and adjusted
  // use their own color spectrum. Both modes now live in [0, 1] (raw = p̂,
  // adjusted = sigmoid-squashed p̃_display).
  const colorFn = useMemo(() => {
    if (!data) return makeColorFn([0.2, 0.4, 0.6, 0.8]);
    const values = data.features
      .map((f) => {
        const p = (f.properties ?? {}) as CountyProps;
        return choroplethMode === "raw" ? p.max_p_hat : p.max_p_tilde_display;
      })
      .filter((v): v is number => v != null && !isNaN(v));
    return makeColorFn(quintileBreaks(values));
  }, [data, choroplethMode]);

  if (!data) return null;

  const styleFn = (feature?: Feature) => {
    const props = (feature?.properties ?? {}) as CountyProps;
    const value = choroplethMode === "raw" ? props.max_p_hat : props.max_p_tilde_display;
    return {
      fillColor: colorFn(value),
      weight: 1,
      color: "#374151",
      fillOpacity: value == null ? 0.25 : 0.7,
    };
  };

  return (
    <GeoJSON
      key={`counties-${choroplethMode}`}
      data={data}
      style={styleFn}
      onEachFeature={(feature, layer) => {
        const props = feature.properties as CountyProps;
        layer.bindTooltip(
          `<b>${props.county ?? "Unknown county"}</b><br/>` +
            `max p̂: ${props.max_p_hat?.toFixed(3) ?? "n/a"}<br/>` +
            `max p̃: ${props.max_p_tilde_display?.toFixed(3) ?? "n/a"}<br/>` +
            `tracts with predictions: ${props.tract_count ?? 0}`,
          { sticky: true },
        );
        layer.on({
          click: () => {
            if (props.county_geoid) {
              setDrillCounty(props.county_geoid);
              pushUrl({ drillCounty: props.county_geoid });
            }
          },
        });
      }}
    />
  );
}
